import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { profileAPI } from '@/api';
import { Card, CardContent, Button, Input, Label, Switch } from '@/components/ui';
import {
  UserCircle, Mail, Shield, Building2, Send, Edit2, Save, X, Key,
  Bell, AlertCircle, Check, Hash, MapPin, Calendar, Eye, EyeOff,
} from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  // Form state
  const [name, setName] = useState(user?.name || '');
  const [telegramId, setTelegramId] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Fetch full profile
  const { data: profileData, refetch: refetchProfile } = useQuery({
    queryKey: ['profile'],
    queryFn: () => profileAPI.get(),
    staleTime: 30000,
  });

  // FIX: was profileData?.data?.data — interceptor already unwraps response.data
  const profile = profileData?.data;

  // Mutations
  const updateMutation = useMutation({
    mutationFn: profileAPI.update,
    onSuccess: () => {
      toast.success('Профиль успешно обновлён');
      setIsEditing(false);
      setIsChangingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      refetchProfile();
      refreshUser?.();
    },
    onError: (error: any) => {
      toast.error(error?.message || error?.errors?.current_password?.[0] || 'Произошла ошибка');
    },
  });

  const notificationsMutation = useMutation({
    mutationFn: profileAPI.updateNotifications,
    onSuccess: () => {
      toast.success('Настройки уведомлений обновлены');
      refetchProfile();
    },
    onError: () => toast.error('Произошла ошибка'),
  });

  const unlinkMutation = useMutation({
    mutationFn: profileAPI.unlinkTelegram,
    onSuccess: () => {
      toast.success('Telegram-аккаунт отвязан');
      refetchProfile();
    },
    onError: () => toast.error('Произошла ошибка'),
  });

  const handleSaveProfile = () => {
    const data: any = {};
    if (name.trim() && name !== user?.name) data.name = name.trim();
    if (telegramId !== (profile?.telegram_id || '')) {
      data.telegram_id = telegramId || null;
    }
    if (Object.keys(data).length > 0) {
      updateMutation.mutate(data);
    } else {
      setIsEditing(false);
    }
  };

  const handleChangePassword = () => {
    if (!currentPassword || !newPassword) {
      toast.error('Заполните все поля');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Пароль должен содержать минимум 8 символов');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Пароли не совпадают');
      return;
    }
    updateMutation.mutate({
      current_password: currentPassword,
      new_password: newPassword,
      new_password_confirmation: confirmPassword,
    });
  };

  const handleNotificationToggle = (key: string, value: boolean) => {
    notificationsMutation.mutate({ [key]: value });
  };

  const startEditing = () => {
    setName(user?.name || '');
    setTelegramId(profile?.telegram_id || '');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
  };

  // Helpers
  const roleName = user?.roles?.[0] || 'user';
  const roleLabel: Record<string, string> = {
    super_admin: 'Super Admin',
    hr_admin: 'HR Admin',
    accountant: 'Бухгалтер',
    branch_manager: 'Менеджер филиала',
    rop_manager: 'ROP-менеджер',
    supervisor: 'Supervisor',
    employee: 'Сотрудник',
    viewer: 'Наблюдатель',
  };
  const roleColors: Record<string, string> = {
    super_admin: 'bg-purple-100 text-purple-800',
    hr_admin: 'bg-blue-100 text-blue-800',
    accountant: 'bg-emerald-100 text-emerald-800',
    branch_manager: 'bg-orange-100 text-orange-800',
    rop_manager: 'bg-teal-100 text-teal-800',
  };

  const notificationSettings = profile?.telegram_link?.notification_settings || {};
  const hasEmployee = !!profile?.employee;
  const isManager = user?.roles?.some((r: string) =>
    ['super_admin', 'hr_admin', 'branch_manager', 'supervisor', 'rop_manager'].includes(r),
  );
  const hasTelegramLinked = !!profile?.telegram_link?.verified_at;
  const initials = (user?.name || 'U')
    .split(' ')
    .map((w: string) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="page-shell space-y-6 max-w-4xl mx-auto">
      {/* ── Header ── */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 p-6 text-white shadow-lg sm:p-8">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-8 -left-8 h-32 w-32 rounded-full bg-white/5 blur-xl" />

        <div className="relative flex flex-col items-center gap-4 sm:flex-row sm:items-end sm:gap-6">
          {/* Avatar */}
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-white/20 text-3xl font-bold backdrop-blur-sm ring-4 ring-white/30 sm:h-24 sm:w-24 sm:text-4xl">
            {initials}
          </div>

          <div className="flex-1 text-center sm:text-left">
            <h1 className="text-2xl font-bold sm:text-3xl">{user?.name}</h1>
            <p className="mt-0.5 text-blue-200">{user?.email}</p>
            <div className="mt-2 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
              <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${roleColors[roleName] || 'bg-white/20 text-white'}`}>
                <Shield className="h-3 w-3" />
                {roleLabel[roleName] || roleName}
              </span>
              {profile?.organization && (
                <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-white">
                  <Building2 className="h-3 w-3" />
                  {profile.organization.name}
                </span>
              )}
              {profile?.branch && (
                <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-white">
                  <MapPin className="h-3 w-3" />
                  {profile.branch.name}
                </span>
              )}
            </div>
          </div>

          {/* Edit Button */}
          {!isEditing && (
            <Button
              variant="ghost"
              onClick={startEditing}
              className="!bg-white/15 !text-white hover:!bg-white/25 backdrop-blur-sm"
            >
              <Edit2 className="h-4 w-4 mr-2" />
              Редактировать
            </Button>
          )}
        </div>
      </div>

      {/* ── Info Cards Row ── */}
      <div className="grid gap-4 sm:grid-cols-3">
        <InfoCard
          icon={<Hash className="h-5 w-5 text-blue-500" />}
          label="ID сотрудника"
          value={profile?.employee?.employee_id || '—'}
        />
        <InfoCard
          icon={<Calendar className="h-5 w-5 text-green-500" />}
          label="Дата регистрации"
          value={profile?.created_at ? new Date(profile.created_at).toLocaleDateString('ru-RU', { year: 'numeric', month: 'long', day: 'numeric' }) : '—'}
        />
        <InfoCard
          icon={<Send className="h-5 w-5 text-sky-500" />}
          label="Telegram"
          value={
            hasTelegramLinked ? (
              <span className="flex items-center gap-1.5 text-green-600 font-medium">
                <Check className="h-4 w-4" /> Подключён
              </span>
            ) : profile?.telegram_id ? (
              <span className="text-amber-600 font-medium">Ожидает подключения</span>
            ) : (
              <span className="text-gray-400">Не подключён</span>
            )
          }
        />
      </div>

      {/* ── Edit form ── */}
      {isEditing && (
        <Card>
          <CardContent className="py-5">
            <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <UserCircle className="h-5 w-5 text-blue-500" />
              Редактирование профиля
            </h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="mb-1.5">Полное имя</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ваше имя"
                />
              </div>
              <div>
                <Label className="mb-1.5">Telegram ID</Label>
                <Input
                  value={telegramId}
                  onChange={(e) => setTelegramId(e.target.value)}
                  placeholder="Например: 123456789"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Напишите @userinfobot — он покажет ваш ID.
                </p>
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              <Button onClick={handleSaveProfile} disabled={updateMutation.isPending}>
                <Save className="h-4 w-4 mr-2" />
                Сохранить
              </Button>
              <Button variant="outline" onClick={cancelEditing}>
                <X className="h-4 w-4 mr-2" />
                Отмена
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Telegram integration ── */}
      <Card>
        <CardContent className="py-5">
          <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Send className="h-5 w-5 text-sky-500" />
            Интеграция Telegram
          </h3>

          {hasTelegramLinked ? (
            <div className="rounded-xl border border-green-200 bg-green-50 p-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-green-800">Telegram подключён</p>
                    <p className="text-sm text-green-600">
                      @{profile?.telegram_link?.telegram_username || 'unknown'} · ID: {profile?.telegram_link?.telegram_user_id}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (confirm('Отвязать Telegram-аккаунт?')) {
                      unlinkMutation.mutate();
                    }
                  }}
                  disabled={unlinkMutation.isPending}
                  className="!text-red-600 !border-red-200 hover:!bg-red-50"
                >
                  Отвязать
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Show current telegram_id */}
              <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-200">
                  <Send className="h-5 w-5 text-slate-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-700">Telegram ID</p>
                  <p className="text-lg font-mono text-slate-900">
                    {profile?.telegram_id || <span className="text-sm text-gray-400 font-sans">Не указан</span>}
                  </p>
                </div>
              </div>

              {profile?.telegram_id ? (
                <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Ожидает подключения</p>
                    <p className="mt-1 text-sm text-amber-700">
                      Откройте Telegram-бота и отправьте команду <code className="rounded bg-amber-100 px-1.5 py-0.5 font-mono text-xs">/start</code>.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
                  <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-800">Telegram не подключён</p>
                    <p className="mt-1 text-sm text-blue-700">
                      Укажите Telegram ID в профиле, затем отправьте боту команду <code className="rounded bg-blue-100 px-1.5 py-0.5 font-mono text-xs">/start</code>.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Notification settings ── */}
      {hasTelegramLinked && (
        <Card>
          <CardContent className="py-5">
            <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Bell className="h-5 w-5 text-amber-500" />
              Настройки уведомлений
            </h3>

            <div className="space-y-6">
              {hasEmployee && (
                <div>
                  <p className="mb-3 text-sm font-medium text-gray-500 uppercase tracking-wider">Личные</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <NotifToggle label="Отметка прихода" checked={notificationSettings.check_in !== false} onChange={(v) => handleNotificationToggle('check_in', v)} />
                    <NotifToggle label="Отметка ухода" checked={notificationSettings.check_out !== false} onChange={(v) => handleNotificationToggle('check_out', v)} />
                    <NotifToggle label="Опоздание" checked={notificationSettings.late !== false} onChange={(v) => handleNotificationToggle('late', v)} />
                    <NotifToggle label="Ранний уход" checked={notificationSettings.early_leave !== false} onChange={(v) => handleNotificationToggle('early_leave', v)} />
                    <NotifToggle label="Повторный проход через терминал" checked={notificationSettings.transit !== false} onChange={(v) => handleNotificationToggle('transit', v)} />
                  </div>
                </div>
              )}

              {isManager && (
                <div>
                  <p className="mb-3 text-sm font-medium text-gray-500 uppercase tracking-wider">Сотрудники</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <NotifToggle label="Сотрудник пришёл" checked={notificationSettings.staff_check_in !== false} onChange={(v) => handleNotificationToggle('staff_check_in', v)} />
                    <NotifToggle label="Сотрудник ушёл" checked={notificationSettings.staff_check_out !== false} onChange={(v) => handleNotificationToggle('staff_check_out', v)} />
                    <NotifToggle label="Сотрудник опоздал" checked={notificationSettings.staff_late !== false} onChange={(v) => handleNotificationToggle('staff_late', v)} />
                    <NotifToggle label="Сотрудник не вышел" checked={notificationSettings.staff_no_show !== false} onChange={(v) => handleNotificationToggle('staff_no_show', v)} />
                    <NotifToggle label="Сотрудник ушёл раньше" checked={notificationSettings.staff_early_leave !== false} onChange={(v) => handleNotificationToggle('staff_early_leave', v)} />
                    <NotifToggle label="Повторный проход сотрудника" checked={notificationSettings.staff_transit !== false} onChange={(v) => handleNotificationToggle('staff_transit', v)} />
                  </div>
                </div>
              )}

              {isManager && (
                <div>
                  <p className="mb-3 text-sm font-medium text-gray-500 uppercase tracking-wider">Система</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <NotifToggle label="Устройство офлайн" checked={notificationSettings.device_offline !== false} onChange={(v) => handleNotificationToggle('device_offline', v)} />
                    <NotifToggle label="Ежедневный отчёт" checked={notificationSettings.daily_summary === true} onChange={(v) => handleNotificationToggle('daily_summary', v)} />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Security ── */}
      <Card>
        <CardContent className="py-5">
          <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Key className="h-5 w-5 text-rose-500" />
            Безопасность
          </h3>

          {!isChangingPassword ? (
            <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div>
                <p className="font-medium text-slate-700">Пароль</p>
                <p className="text-sm text-gray-500">Измените пароль аккаунта</p>
              </div>
              <Button variant="outline" onClick={() => setIsChangingPassword(true)}>
                Изменить
              </Button>
            </div>
          ) : (
            <div className="max-w-md space-y-4">
              <div>
                <Label className="mb-1.5">Текущий пароль</Label>
                <div className="relative">
                  <Input
                    type={showCurrentPw ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Текущий пароль"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPw(!showCurrentPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showCurrentPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="mb-1.5">Новый пароль</Label>
                <div className="relative">
                  <Input
                    type={showNewPw ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Минимум 8 символов"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPw(!showNewPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="mb-1.5">Подтвердите новый пароль</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Повторите новый пароль"
                  autoComplete="new-password"
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button onClick={handleChangePassword} disabled={updateMutation.isPending}>
                  <Save className="h-4 w-4 mr-2" />
                  Сохранить
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsChangingPassword(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                >
                  Отмена
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ─── Helper components ─── */

function InfoCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="py-4 flex items-center gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100">
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-xs text-gray-500">{label}</p>
          <p className="text-sm font-semibold text-slate-800 truncate">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function NotifToggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-100 bg-white px-4 py-3">
      <Label className="!text-sm !text-slate-700 !font-normal">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
